import { eq, or } from 'tio-ui/utilities';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import {
  PhCheckCircle,
  PhCloudArrowUp,
  PhFilePdf,
  PhInfo,
  PhSparkle,
  PhUploadSimple,
  PhWarningCircle,
  PhX,
  PhXCircle,
} from 'ember-phosphor-icons';
import Popover from 'tio-reimagined/components/popover';
import FileUploader from '../../../file-uploader.gts';
import AcceptedBanner from './accepted-banner.gts';
import FlaggedBanner from './flagged-banner.gts';
import NotValidatedBanner from './not-validated-banner.gts';
import type { DocumentType } from '../../../../types/tuition-assistance.ts';
import type { TOC } from '@ember/component/template-only';
import type TasAssetModel from '../../../../models/tas-asset.ts';

/**
 * Per-row state for a single required document type.
 *
 * Status transitions (matches the Figma "Upload by Type v2" prototype):
 *
 *   empty
 *     ↓ user picks file
 *   uploading            (S3 PUT in flight, brief)
 *     ↓
 *   analyzing            (waiting for AI ServiceResponse)
 *     ↓
 *   valid | flagged | not_validated | error
 *
 * Per the Figma flow:
 *   - "valid" briefly shows a teal border + green check chip then settles to
 *     the gray-bordered "uploaded" look (file chip visible).
 *   - "flagged" keeps the row red and shows a discard / use-anyways banner.
 *   - "accepted" keeps the row amber to reflect the employee override warning.
 *   - "not_validated" keeps the row amber and shows a use-anyways / discard
 *     banner (used when AI couldn't classify the document).
 *   - All states preserve the asset on the application — discards are explicit.
 */
export type DocumentRowStatus =
  | 'empty'
  | 'uploading'
  | 'analyzing'
  | 'valid'
  | 'accepted'
  | 'flagged'
  | 'not_validated'
  | 'error';

/**
 * Why a row is flagged. "content" allows `use anyways` (Figma: receipt missing
 * an amount); "invalid_type" is discard-only (Figma: .mov upload).
 */
export type FlagKind = 'content' | 'invalid_type';

export interface DocumentAssetState {
  asset: TasAssetModel;
  status: DocumentRowStatus;
  feedback: string | null;
  flagKind?: FlagKind;
}

export interface DocumentRowState {
  status: DocumentRowStatus;
  asset: TasAssetModel | null;
  assets: TasAssetModel[];
  assetStates: DocumentAssetState[];
  feedback: string | null;
  flagKind?: FlagKind;
}

interface DocumentRowSignature {
  Args: {
    docType: DocumentType;
    /** Admin-configured custom label. When provided, displayed instead of the
     * i18n translation for the document type. */
    label?: string;
    state: DocumentRowState;
    onUpload: (file: unknown) => void;
    onRemove: (asset?: TasAssetModel) => void;
    onUseAnyways: (asset?: TasAssetModel) => void;
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Border treatment driven by row state. Mirrors the Figma frames:
 *   analyzing → lavender, valid → teal, accepted/not_validated → amber,
 *   flagged/error → red, otherwise neutral gray.
 */
const borderClass = (status: DocumentRowStatus): string => {
  switch (status) {
    case 'analyzing':
    case 'uploading':
      return 'border-violet-300';
    case 'valid':
      return 'border-teal-400';
    case 'accepted':
    case 'not_validated':
      return 'border-amber-400';
    case 'flagged':
    case 'error':
      return 'border-red-500';
    default:
      return 'border-tio-gray-200';
  }
};

const TuitionAssistanceFormsCategorizedUploadDocumentRow: TOC<DocumentRowSignature> = <template>
  <div
    class="flex flex-col overflow-hidden rounded-xl border bg-white {{borderClass @state.status}}"
    data-status={{@state.status}}
    data-test-doc-row={{@docType}}
    ...attributes
  >
    <div class="flex items-start justify-between gap-3 p-4">
      <div class="flex min-w-0 flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="font-medium text-base">
            {{#if @label}}
              {{@label}}
            {{else}}
              {{t (concat "tas.attachments.document_types." @docType ".label")}}
            {{/if}}
          </span>
          <Popover @placement="bottom-start" @triggerMode="click">
            <:trigger as |pop|>
              <button
                type="button"
                class="text-tio-gray-500 hover:text-tio-gray-700"
                aria-label={{t (concat "tas.attachments.document_types." @docType ".hint")}}
                {{pop.register}}
                {{on "click" pop.toggle}}
              >
                <PhInfo @size="16" />
              </button>
            </:trigger>
            <:body>
              <div class="max-w-xs p-3 text-sm">
                {{! Each language file ships an `elements:` array of bullets.
                  Skip for "general" which has no elements defined. }}
                {{#if (eq @docType "general")}}
                  <p class="font-semibold text-tio-gray-900">
                    {{#if @label}}
                      {{@label}}
                    {{else}}
                      {{t (concat "tas.attachments.document_types." @docType ".label")}}
                    {{/if}}
                  </p>
                  <p class="mt-2 text-tio-gray-700">
                    {{t (concat "tas.attachments.document_types." @docType ".hint")}}
                  </p>
                {{else}}
                  <p class="mb-2 font-semibold text-tio-gray-900">
                    {{t "tas.attachments.tooltip_must_include"}}
                  </p>
                  <ul class="ml-4 list-disc text-tio-gray-700">
                    <li>{{t "tas.attachments.tooltip_requirements.student_full_name"}}</li>
                    <li>{{t "tas.attachments.tooltip_requirements.institution"}}</li>
                    {{#if (eq @docType "receipt")}}
                      <li>{{t "tas.attachments.tooltip_requirements.date_of_payment"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.items_paid_for"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.payment_amount"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.payment_method"}}</li>
                    {{else if (eq @docType "certificate")}}
                      <li>{{t "tas.attachments.tooltip_requirements.date_of_completion"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.credential_name"}}</li>
                    {{else}}
                      <li>{{t "tas.attachments.tooltip_requirements.dates_of_attendance"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.course_name"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.grade"}}</li>
                    {{/if}}
                  </ul>
                  <p class="mt-2 text-tio-gray-700">
                    {{t "tas.attachments.tooltip_note"}}
                  </p>
                {{/if}}
              </div>
            </:body>
          </Popover>
        </div>

        {{! Inline status chip below the title (matches Figma A2/A3). }}
        {{#if (eq @state.status "analyzing")}}
          <span
            class="inline-flex w-fit items-center gap-1 rounded bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
          >
            <PhSparkle @size="12" class="animate-pulse" />
            <span>analyzing</span>
            <span class="flex gap-px">
              <span class="animate-bounce delay-0 duration-1000">.</span>
              <span class="animate-bounce delay-200 duration-1000">.</span>
              <span class="animate-bounce delay-400 duration-1000">.</span>
            </span>
          </span>
        {{else if (eq @state.status "uploading")}}
          <span
            class="inline-flex w-fit items-center gap-1 rounded bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700"
          >
            <PhCloudArrowUp @size="12" class="animate-pulse" />
            <span>uploading</span>
            <span class="flex gap-px">
              <span class="animate-bounce delay-0 duration-1000">.</span>
              <span class="animate-bounce delay-200 duration-1000">.</span>
              <span class="animate-bounce delay-400 duration-1000">.</span>
            </span>
          </span>
        {{else if (eq @state.status "valid")}}
          <span
            class="inline-flex w-fit items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
          >
            <PhCheckCircle @size="12" @weight="fill" />
            {{t "tas.attachments.valid"}}
          </span>
        {{else if (eq @state.status "accepted")}}
          <span
            class="inline-flex w-fit items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
          >
            <PhCheckCircle @size="12" @weight="fill" />
            {{t "tas.attachments.accepted"}}
          </span>
        {{else if (eq @state.status "flagged")}}
          <span
            class="inline-flex w-fit items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
          >
            <PhWarningCircle @size="12" @weight="fill" />
            {{t "tas.attachments.flagged"}}
          </span>
        {{else if (eq @state.status "error")}}
          <span
            class="inline-flex w-fit items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
          >
            <PhXCircle @size="12" @weight="fill" />
            {{t "tas.attachments.error"}}
          </span>
        {{/if}}

        {{! Uploaded file chips — present whenever assets exist, regardless of
          validation status. Multiple files can belong to one document bucket. }}
        {{#each @state.assets as |asset|}}
          <div
            class="mt-1 inline-flex w-fit max-w-full items-center gap-2 rounded-lg bg-tio-gray-50 px-2 py-1 text-xs"
          >
            <PhFilePdf @size="14" class="shrink-0 text-tio-gray-600" />
            <span class="truncate" title={{asset.filename}}>
              {{asset.filename}}
            </span>
            <button
              type="button"
              class="shrink-0 text-tio-gray-500 hover:text-red-600"
              aria-label={{t "tas.attachments.remove"}}
              {{on "click" (fn @onRemove asset)}}
            >
              <PhX @size="12" />
            </button>
          </div>
        {{/each}}
      </div>

      {{! Right-side upload control. Full button when empty (Figma A1), icon-only
        once a file exists or is in flight (Figma A2+). }}
      <div class="flex shrink-0 items-center gap-2">
        <FileUploader @didUploadFile={{@onUpload}} as |uploader|>
          <input
            id={{uploader.uniqueId}}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            {{uploader.queue.selectFile}}
            class="hidden"
            disabled={{or @disabled (eq @state.status "uploading")}}
          />
          {{#if (eq @state.status "empty")}}
            <label
              for={{uploader.uniqueId}}
              class="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-ocean-600 px-3 py-1.5 text-sm font-medium text-ocean-600 hover:border-ocean-800 hover:text-ocean-800
                {{if @disabled 'opacity-50 cursor-not-allowed'}}"
              data-test-upload-button
            >
              <PhUploadSimple @size="14" />
              {{t "tas.attachments.upload"}}
            </label>
          {{else}}
            <label
              for={{uploader.uniqueId}}
              class="inline-flex cursor-pointer items-center justify-center rounded-md border border-tio-gray-400 p-1.5 text-tio-gray-500 hover:border-tio-gray-600 hover:text-tio-gray-700
                {{if @disabled 'opacity-50 cursor-not-allowed'}}"
              aria-label={{t "tas.attachments.upload"}}
              data-test-upload-button-icon
            >
              <PhUploadSimple @size="14" />
            </label>
          {{/if}}
        </FileUploader>
      </div>
    </div>

    {{! Expanded banners. Rendered below the row body (Figma A10 / B4 / 4359
      and 4362 frames). The banner owns its own border bar + actions. }}
    {{#each @state.assetStates as |assetState|}}
      {{#if (eq assetState.status "flagged")}}
        <FlaggedBanner
          @filename={{assetState.asset.filename}}
          @reason={{assetState.feedback}}
          @flagKind={{if (eq assetState.flagKind "invalid_type") "invalid_type" "content"}}
          @onDiscard={{fn @onRemove assetState.asset}}
          @onUseAnyways={{fn @onUseAnyways assetState.asset}}
        />
      {{else if (eq assetState.status "not_validated")}}
        <NotValidatedBanner
          @filename={{assetState.asset.filename}}
          @onUseAnyways={{fn @onUseAnyways assetState.asset}}
          @onDiscard={{fn @onRemove assetState.asset}}
        />
      {{else if (eq assetState.status "accepted")}}
        <AcceptedBanner @filename={{assetState.asset.filename}} />
      {{else if (eq assetState.status "error")}}
        <p
          class="border-t border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 last:rounded-b-xl"
        >
          {{assetState.asset.filename}}:
          {{assetState.feedback}}
        </p>
      {{/if}}
    {{/each}}
  </div>
</template>;

export default TuitionAssistanceFormsCategorizedUploadDocumentRow;
